// EmojiPicker.js — Panneau emojis + stickers Worms pour les chats (communauté, privé, groupe).
// Le PARENT gère l'ouverture et l'ancre (l'englober dans un conteneur position:relative).
//   <span style={{position:'relative'}}>
//     <button onClick={toggle}>😀</button>
//     {open && <EmojiPicker onEmoji={insert} onSticker={sendSticker} onClose={close} />}
//   </span>
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import { WORM_STICKERS } from './WormSticker';
import { GIF_ENABLED, searchGif, mediaUrl } from '../services/chatMedia';

const CATS = [
  { key: 'worms', label: '🪱' },
  { key: 'smileys', label: '😀', emojis: '😀 😃 😄 😁 😆 😅 😂 🤣 😊 🙂 🙃 😉 😌 😍 🥰 😘 😗 😙 😚 😋 😛 😝 😜 🤪 🤨 🧐 🤓 😎 🥳 😏 😒 😞 😔 😟 😕 🙁 😣 😖 😫 😩 🥺 😢 😭 😤 😠 😡 🤬 🤯 😳 🥵 🥶 😱 😨 😰 😥 😓 🤗 🤔 🤭 🤫 🤥 😶 😐 😑 😬 🙄 😯 😦 😧 😮 😲 🥱 😴 🤤 😪 😵 🤐 🥴 🤢 🤮 🤧 😷 🤒 🤕 🤠 😈 👿 👻 💀 👽 🤖 💩'.split(' ') },
  { key: 'gestures', label: '👍', emojis: '👍 👎 👌 ✌️ 🤞 🤟 🤘 🤙 👏 🙌 🙏 💪 🤝 👋 🖐️ ✋ 🤚 👆 👇 👈 👉 ☝️ ✊ 👊 🤛 🤜 💅 🤳'.split(' ') },
  { key: 'hearts', label: '❤️', emojis: '❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 ❤️‍🔥 💕 💞 💓 💗 💖 💘 💝 💯 💢 💥 💫 ⭐ 🌟 ✨ 🔥 🎉 🎊'.split(' ') },
  { key: 'games', label: '🎮', emojis: '🎮 🕹️ 👾 🎯 🎲 🃏 🀄 🎰 🏆 🥇 🥈 🥉 ⚔️ 🛡️ 🚀 💣 🧨 🪱 🐛 🎖️ 🏅 👑 🎁 🍺 🍕 ⚽ 🏀'.split(' ') },
];

const Panel = styled.div`
  position: absolute; bottom: calc(100% + .5rem); left: 0; z-index: 30;
  width: min(320px, 86vw); max-width: calc(100vw - 1.5rem); background: ${theme.colors.bgCard}; border: 1px solid ${theme.colors.borderHover};
  border-radius: 14px; box-shadow: ${theme.shadows.card}; overflow: hidden; display: flex; flex-direction: column;
`;
const TabRow = styled.div`
  display: flex; gap: .1rem; padding: .3rem; border-bottom: 1px solid ${theme.colors.border};
  button { flex: 1; background: none; border: none; cursor: pointer; font-size: 1.05rem; padding: .3rem; border-radius: 8px; }
  button.on { background: ${theme.colors.bgPrimary}; }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(8, 1fr); gap: .1rem; padding: .5rem; max-height: 200px; overflow-y: auto;
  button { background: none; border: none; cursor: pointer; font-size: 1.3rem; padding: .2rem; border-radius: 8px; line-height: 1; }
  button:hover { background: ${theme.colors.bgPrimary}; }
`;
const Stickers = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: .3rem; padding: .5rem; max-height: 210px; overflow-y: auto;
  button { background: ${theme.colors.bgPrimary}; border: 1px solid ${theme.colors.border}; border-radius: 12px; cursor: pointer;
    display: flex; flex-direction: column; align-items: center; gap: .1rem; padding: .35rem .2rem; }
  button:hover { border-color: ${theme.colors.borderHover}; }
  .w { font-size: 1.9rem; line-height: 1; position: relative; }
  .f { position: absolute; right: -6px; top: -4px; font-size: .95rem; }
  .l { font-size: .64rem; color: ${theme.colors.textMuted}; }
`;

export default function EmojiPicker({ onEmoji, onSticker, onGif, onClose }) {
  const [cat, setCat] = useState('smileys');
  const [gifQ, setGifQ] = useState('');
  const [gifs, setGifs] = useState([]);
  const ref = useRef(null);
  // Fermer au clic extérieur.
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose && onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);
  // Recherche GIF (débouncée) — seulement si activé + provider configuré.
  useEffect(() => {
    if (cat !== 'gif' || !GIF_ENABLED) return undefined;
    const t = setTimeout(() => { searchGif(gifQ || 'lol').then(setGifs); }, 350);
    return () => clearTimeout(t);
  }, [cat, gifQ]);

  // L'onglet GIF n'apparaît QUE si la fonctionnalité est activée (flag de build + clé backend).
  const tabs = GIF_ENABLED && onGif ? [...CATS, { key: 'gif', label: 'GIF' }] : CATS;
  const current = CATS.find((c) => c.key === cat);
  return (
    <Panel ref={ref} onClick={(e) => e.stopPropagation()}>
      <TabRow>
        {tabs.map((c) => (
          <button key={c.key} className={cat === c.key ? 'on' : ''} onClick={() => setCat(c.key)} title={c.key} style={c.key === 'gif' ? { fontSize: '.7rem', fontWeight: 800 } : undefined}>{c.label}</button>
        ))}
      </TabRow>
      {cat === 'gif' ? (
        <div style={{ padding: '.4rem' }}>
          <input value={gifQ} onChange={(e) => setGifQ(e.target.value)} placeholder="Rechercher un GIF…" style={{ width: '100%', background: theme.colors.bgPrimary, color: theme.colors.textPrimary, border: `1px solid ${theme.colors.border}`, borderRadius: 999, padding: '.35rem .7rem', fontSize: '.8rem', marginBottom: '.4rem' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '.25rem', maxHeight: 200, overflowY: 'auto' }}>
            {gifs.map((g) => (
              <button key={g.id} onClick={() => { onGif(g.url); onClose && onClose(); }} style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}>
                <img src={mediaUrl(g.preview)} alt="gif" style={{ width: '100%', borderRadius: 6, display: 'block' }} />
              </button>
            ))}
          </div>
        </div>
      ) : cat === 'worms' ? (
        <Stickers>
          {WORM_STICKERS.map((s) => (
            <button key={s.id} title={s.label} onClick={() => { onSticker && onSticker(s.id); onClose && onClose(); }}>
              <span className="w">🪱<span className="f">{s.face}</span></span>
              <span className="l">{s.label}</span>
            </button>
          ))}
        </Stickers>
      ) : (
        <Grid>
          {(current.emojis || []).map((e, i) => (
            <button key={e + i} onClick={() => { onEmoji && onEmoji(e); }}>{e}</button>
          ))}
        </Grid>
      )}
    </Panel>
  );
}
